import React, { useState, useEffect } from 'react';
import { downloadFile } from './authFetch';

export default function ProductSync() {
  const [mode, setMode] = useState('without-modifiers');
  const [classes, setClasses] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedCatalogId, setSelectedCatalogId] = useState('');

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedGuids, setSelectedGuids] = useState(new Set());
  const [selectedMenus, setSelectedMenus] = useState(new Set());
  const [selectedMenuGroups, setSelectedMenuGroups] = useState(new Set());

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const [imageJob, setImageJob] = useState(null); // { jobId, status, total, downloaded, failedCount, parts, error }
  const [preparingImages, setPreparingImages] = useState(false);

  const isModifierMode = mode === 'with-modifiers';

  useEffect(() => {
    fetchDropdownOptions();
  }, []);

  async function fetchDropdownOptions() {
    try {
      const [classesRes, catalogsRes] = await Promise.all([
        fetch('/api/product-sync/classes'),
        fetch('/api/product-sync/catalogs'),
      ]);
      setClasses(await classesRes.json());
      setCatalogs(await catalogsRes.json());
    } catch (err) {
      setError(err.message);
    }
  }

  async function fetchProducts() {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const endpoint = isModifierMode ? 'modifier-products' : 'products';
      const response = await fetch(`/api/product-sync/${endpoint}?${params}`);
      if (!response.ok) throw new Error('Failed to fetch products');
      const data = await response.json();
      setProducts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchProducts();
    setSelectedGuids(new Set());
    setSelectedMenus(new Set());
    setSelectedMenuGroups(new Set());
    setSyncResult(null);
  }, [search, mode]);

  const menuOptions = [...new Set(products.map((p) => p.menu_name).filter(Boolean))].sort();
  const menuGroupOptions = [...new Set(
    products
      .filter((p) => selectedMenus.size === 0 || selectedMenus.has(p.menu_name))
      .map((p) => p.menu_group_name)
      .filter(Boolean)
  )].sort();

  const filteredProducts = products.filter((p) =>
    (selectedMenus.size === 0 || selectedMenus.has(p.menu_name)) &&
    (selectedMenuGroups.size === 0 || selectedMenuGroups.has(p.menu_group_name))
  );

  function toggleProduct(guid) {
    setSelectedGuids((prev) => {
      const next = new Set(prev);
      if (next.has(guid)) next.delete(guid);
      else next.add(guid);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelectedGuids((prev) => {
      if (prev.size === filteredProducts.length) return new Set();
      return new Set(filteredProducts.map((p) => p.guid));
    });
  }

  async function handleSync() {
    if (!selectedCatalogId || selectedGuids.size === 0) return;
    if (!isModifierMode && !selectedClassId) return;
    try {
      setSyncing(true);
      setSyncResult(null);
      const endpoint = isModifierMode ? 'insert-with-modifiers' : 'insert';
      const body = isModifierMode
        ? { productGuids: Array.from(selectedGuids), catalogId: selectedCatalogId }
        : { productGuids: Array.from(selectedGuids), classId: selectedClassId, catalogId: selectedCatalogId };
      const response = await fetch(`/api/product-sync/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Sync failed');
      setSyncResult(data);
      setSelectedGuids(new Set());
    } catch (err) {
      setSyncResult({ written: 0, failed: [], error: err.message });
    } finally {
      setSyncing(false);
    }
  }

  async function handlePrepareImageDownloads() {
    try {
      setPreparingImages(true);
      const response = await fetch('/api/product-sync/image-downloads', { method: 'POST' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to start image download job');
      setImageJob({ jobId: data.jobId, status: 'in-progress', total: 0, downloaded: 0, failedCount: 0, parts: null });
      pollImageJobStatus(data.jobId);
    } catch (err) {
      setImageJob({ status: 'error', error: err.message });
    } finally {
      setPreparingImages(false);
    }
  }

  function pollImageJobStatus(jobId) {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`/api/product-sync/image-downloads/${jobId}/status`);
        if (response.status === 404) {
          clearInterval(interval);
          setImageJob({ status: 'error', error: 'Job expired, please try again' });
          return;
        }
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to check job status');
        setImageJob({ jobId, ...data });
        if (data.status !== 'in-progress') clearInterval(interval);
      } catch (err) {
        clearInterval(interval);
        setImageJob({ status: 'error', error: err.message });
      }
    }, 2000);
  }

  const canSync = selectedCatalogId && (isModifierMode || selectedClassId) && selectedGuids.size > 0 && !syncing;

  const syncActionBar = (
    <div className="sync-action-bar">
      <button disabled={!canSync} onClick={handleSync}>
        {syncing ? 'Syncing...' : `Sync Selected to Firestore (${selectedGuids.size})`}
      </button>
      {!isModifierMode && !selectedClassId && <span className="info-message">Select a class first</span>}
      {(isModifierMode || selectedClassId) && !selectedCatalogId && <span className="info-message">Select a catalog first</span>}
    </div>
  );

  return (
    <div className="product-sync-container">
      <h2>Product Sync</h2>
      <p className="subtitle">
        {isModifierMode
          ? "Sync products with modifiers into Firestore — ProductClass is auto-resolved from each product's category"
          : 'Sync products without modifiers into Firestore'}
      </p>

      <div className="mode-toggle">
        <button
          className={!isModifierMode ? 'mode-btn active' : 'mode-btn'}
          onClick={() => setMode('without-modifiers')}
        >
          Without Modifiers
        </button>
        <button
          className={isModifierMode ? 'mode-btn active' : 'mode-btn'}
          onClick={() => setMode('with-modifiers')}
        >
          With Modifiers
        </button>
      </div>

      {syncActionBar}

      <div className="image-download-bar">
        <button disabled={preparingImages || imageJob?.status === 'in-progress'} onClick={handlePrepareImageDownloads}>
          {imageJob?.status === 'in-progress' ? 'Preparing Images...' : 'Prepare Image Downloads'}
        </button>
        {imageJob?.status === 'in-progress' && (
          <span className="info-message">{imageJob.downloaded}/{imageJob.total} images fetched</span>
        )}
        {imageJob?.status === 'error' && (
          <span className="error-inline">{imageJob.error}</span>
        )}
        {imageJob?.status === 'ready' && imageJob.parts.length === 0 && (
          <span className="info-message">No images to download</span>
        )}
        {imageJob?.status === 'ready' && imageJob.parts.length > 0 && (
          <div className="image-download-parts">
            {imageJob.failedCount > 0 && (
              <span className="info-message">{imageJob.failedCount} image(s) failed to download</span>
            )}
            {imageJob.parts.map((part) => (
              <button
                key={part.index}
                type="button"
                className="download-part-btn"
                onClick={() => downloadFile(
                  `/api/product-sync/image-downloads/${imageJob.jobId}/parts/${part.index}`,
                  `product-images-part-${part.index + 1}.zip`
                )}
              >
                Download Part {part.index + 1} ({part.fileCount} images, {part.sizeMB} MB)
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="filter-bar">
        {!isModifierMode && (
          <select value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)}>
            <option value="">Select Class...</option>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}

        <select value={selectedCatalogId} onChange={(e) => setSelectedCatalogId(e.target.value)}>
          <option value="">Select Catalog...</option>
          {catalogs.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <input
          type="text"
          placeholder="Search products by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <MultiSelectFilter
          label="Menu"
          options={menuOptions}
          selected={selectedMenus}
          onChange={setSelectedMenus}
        />

        <MultiSelectFilter
          label="Menu Group"
          options={menuGroupOptions}
          selected={selectedMenuGroups}
          onChange={setSelectedMenuGroups}
        />
      </div>

      {error && <div className="error">✕ {error}</div>}
      {loading && <p className="loading">Loading...</p>}

      {!loading && !error && (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={filteredProducts.length > 0 && selectedGuids.size === filteredProducts.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Name</th>
                <th>Price</th>
                <th>Menu</th>
                <th>Menu Group</th>
                <th>SKU</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((p) => (
                <tr key={p.guid}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedGuids.has(p.guid)}
                      onChange={() => toggleProduct(p.guid)}
                    />
                  </td>
                  <td className="name-cell">{p.name}</td>
                  <td className="price-cell">${(p.price || 0).toFixed(2)}</td>
                  <td>{p.menu_name}</td>
                  <td>{p.menu_group_name}</td>
                  <td>{p.sku || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredProducts.length === 0 && <p className="empty-message">No products found</p>}
        </div>
      )}

      {syncActionBar}

      {syncResult && (
        <div className={syncResult.error || syncResult.failed?.length ? 'error' : 'success-message'}>
          {syncResult.error
            ? `Sync failed: ${syncResult.error}`
            : `Wrote ${syncResult.written} product(s), ${syncResult.failed.length} failed`}
          {syncResult.failed && syncResult.failed.length > 0 && (
            <ul>
              {syncResult.failed.map((f) => (
                <li key={f.guid}>{f.guid}: {f.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function MultiSelectFilter({ label, options, selected, onChange }) {
  function toggleOption(option) {
    const next = new Set(selected);
    if (next.has(option)) next.delete(option);
    else next.add(option);
    onChange(next);
  }

  return (
    <details className="multiselect-filter">
      <summary>{label}{selected.size > 0 ? ` (${selected.size})` : ''}</summary>
      <div className="multiselect-panel">
        {selected.size > 0 && (
          <button type="button" className="multiselect-clear" onClick={() => onChange(new Set())}>
            Clear
          </button>
        )}
        {options.map((option) => (
          <label key={option} className="multiselect-option">
            <input
              type="checkbox"
              checked={selected.has(option)}
              onChange={() => toggleOption(option)}
            />
            {option}
          </label>
        ))}
        {options.length === 0 && <p className="multiselect-empty">No options</p>}
      </div>
    </details>
  );
}
